import {type MessageDescriptor} from '@lingui/core'
import {msg} from '@lingui/core/macro'

import {type GoalStepResults} from '#/screens/Onboarding/state'

export const LEARNING_STAGES = [
  'Preschool / Kindergarten',
  'Primary school',
  'Middle School',
  'High School',
  'University',
  'Self-learner (autodidact)',
  'Lifelong Learner',
] as const

export const STAGES_NEEDING_GRADE_LEVEL: ReadonlyArray<string> = [
  'Primary school',
  'Middle School',
  'High School',
]

export const FAMILY_STAGES = [
  'Preschool / Kindergarten',
  'Primary school',
  'Middle School',
  'High School',
  'University',
] as const

export const AUDIENCE_TARGETS = [
  'Preschool / Kindergarten',
  'Primary school',
  'Middle School',
  'High School',
  'University',
  'Self-learners',
  'Professionals',
  'Parents',
] as const

export const MAX_AUDIENCE_TARGETS = 2

export const MAX_TOPICS: Record<string, number> = {
  learner: 5,
  parent: 5,
  creator: 8,
  organisation: 10,
}

export type CreatorRole = {
  id: string
  icon: string
  label: MessageDescriptor
  desc: MessageDescriptor
}

export const CREATOR_ROLES: CreatorRole[] = [
  {
    id: 'tutor',
    icon: '📖',
    label: msg`Tutor`,
    desc: msg`Walks learners step-by-step through a subject's core knowledge and skills.`,
  },
  {
    id: 'coach',
    icon: '🏆',
    label: msg`Coach`,
    desc: msg`Helps someone build a skill, habit, or mindset through ongoing practice and feedback.`,
  },
  {
    id: 'mentor',
    icon: '🧭',
    label: msg`Mentor`,
    desc: msg`Draws on lived experience to guide someone's long-term personal or professional growth.`,
  },
  {
    id: 'trainer',
    icon: '🏋️',
    label: msg`Trainer`,
    desc: msg`Builds physical, technical, or operational capability through hands-on practice.`,
  },
  {
    id: 'guide',
    icon: '🗺️',
    label: msg`Guide`,
    desc: msg`Leads others toward clarity, meaning, or inner growth.`,
  },
  {
    id: 'artist',
    icon: '🎨',
    label: msg`Artist`,
    desc: msg`Teaches and expresses ideas through a creative craft.`,
  },
  {
    id: 'speaker',
    icon: '🎤',
    label: msg`Speaker`,
    desc: msg`Delivers ideas live or recorded to inspire, inform, or persuade an audience.`,
  },
  {
    id: 'creator',
    icon: '🎬',
    label: msg`Creator`,
    desc: msg`Shares knowledge or ideas through ongoing content people follow.`,
  },
  {
    id: 'researcher',
    icon: '🔬',
    label: msg`Researcher`,
    desc: msg`Investigates a subject in depth and shares original findings or analysis.`,
  },
  {
    id: 'consultant',
    icon: '💼',
    label: msg`Consultant`,
    desc: msg`Applies specialized expertise to solve a specific problem for clients.`,
  },
  {
    id: 'podcaster',
    icon: '🎙️',
    label: msg`Podcaster`,
    desc: msg`Shares knowledge through long-form spoken conversation, solo or with guests.`,
  },
  {
    id: 'edutainment',
    icon: '🎭',
    label: msg`Edutainment Creator`,
    desc: msg`Blends education with entertainment to make learning memorable.`,
  },
  {
    id: 'ai_skill_trainer',
    icon: '🤖',
    label: msg`AI Skill Trainer`,
    desc: msg`Teaches people how to actually use AI tools in their day-to-day work.`,
  },
]

export type OrgTypeCategory =
  | 'Educational Institutions'
  | 'Training & Tutoring'
  | 'Knowledge & Culture'
  | 'Publishing & Media'
  | 'Corporate & Professional'
  | 'Other'

export type OrgType = {
  id: string
  label: MessageDescriptor
  category: OrgTypeCategory
  desc: MessageDescriptor
}

export const ORG_TYPES: OrgType[] = [
  {
    id: 'school_primary',
    label: msg`Primary School`,
    category: 'Educational Institutions',
    desc: msg`A primary or elementary school managing official academic content from grades 1-6.`,
  },
  {
    id: 'school_middle',
    label: msg`Middle School`,
    category: 'Educational Institutions',
    desc: msg`A middle/preparatory school serving grades 7-9.`,
  },
  {
    id: 'school_secondary',
    label: msg`Secondary School`,
    category: 'Educational Institutions',
    desc: msg`A secondary school preparing students for national exams (BAC, A-levels, etc.).`,
  },
  {
    id: 'school_private',
    label: msg`Private School`,
    category: 'Educational Institutions',
    desc: msg`An independent or international private school with its own curriculum.`,
  },
  {
    id: 'university',
    label: msg`University / Faculty`,
    category: 'Educational Institutions',
    desc: msg`A higher-education institution publishing course content and academic resources.`,
  },
  {
    id: 'grande_ecole',
    label: msg`Grande Ecole / Institute`,
    category: 'Educational Institutions',
    desc: msg`A selective professional school (engineering, business, architecture, medicine, etc.).`,
  },
  {
    id: 'vocational',
    label: msg`Vocational School`,
    category: 'Educational Institutions',
    desc: msg`A technical or vocational training centre delivering trade and professional certifications.`,
  },
  {
    id: 'centre_formation',
    label: msg`Centre de Formation`,
    category: 'Training & Tutoring',
    desc: msg`An accredited training centre offering professional development and skills courses.`,
  },
  {
    id: 'tutoring_center',
    label: msg`Tutoring / Study Center`,
    category: 'Training & Tutoring',
    desc: msg`A private academic support centre offering reinforcement and exam preparation.`,
  },
  {
    id: 'language_school',
    label: msg`Language School`,
    category: 'Training & Tutoring',
    desc: msg`A specialised institution offering foreign-language instruction and certificates.`,
  },
  {
    id: 'bootcamp',
    label: msg`Bootcamp / Coding School`,
    category: 'Training & Tutoring',
    desc: msg`An intensive short-format program focused on practical tech and digital skills.`,
  },
  {
    id: 'online_school',
    label: msg`Online School / EdTech`,
    category: 'Training & Tutoring',
    desc: msg`A fully digital education platform offering structured courses or self-paced learning.`,
  },
  {
    id: 'library',
    label: msg`Library`,
    category: 'Knowledge & Culture',
    desc: msg`A public or institutional library sharing curated reading lists and learning resources.`,
  },
  {
    id: 'museum',
    label: msg`Museum / Cultural Centre`,
    category: 'Knowledge & Culture',
    desc: msg`A museum, cultural house, or arts centre with an educational or outreach program.`,
  },
  {
    id: 'maison_jeunes',
    label: msg`Maison des Jeunes`,
    category: 'Knowledge & Culture',
    desc: msg`A youth centre providing civic, cultural, and educational programming.`,
  },
  {
    id: 'cultural_assoc',
    label: msg`Cultural Association`,
    category: 'Knowledge & Culture',
    desc: msg`A non-profit cultural association organising educational events and community outreach.`,
  },
  {
    id: 'mosque_school',
    label: msg`Mosque / Religious School`,
    category: 'Knowledge & Culture',
    desc: msg`A religious institution with an education mission offering values-based learning.`,
  },
  {
    id: 'book_publisher',
    label: msg`Book Publisher`,
    category: 'Publishing & Media',
    desc: msg`An academic or trade publisher offering textbooks and educational content.`,
  },
  {
    id: 'bookstore',
    label: msg`Bookstore`,
    category: 'Publishing & Media',
    desc: msg`A bookshop curating and promoting educational titles and reading recommendations.`,
  },
  {
    id: 'media_org',
    label: msg`Educational Media`,
    category: 'Publishing & Media',
    desc: msg`A broadcast or digital media organisation producing educational content.`,
  },
  {
    id: 'press',
    label: msg`Press / Educational Journal`,
    category: 'Publishing & Media',
    desc: msg`A publication focused on education news, research, or student journalism.`,
  },
  {
    id: 'company_hr',
    label: msg`Company (L&D / HR)`,
    category: 'Corporate & Professional',
    desc: msg`A business using Coapp to deliver internal training or employee development.`,
  },
  {
    id: 'edtech_company',
    label: msg`EdTech Company`,
    category: 'Corporate & Professional',
    desc: msg`A technology company building education products or platforms.`,
  },
  {
    id: 'ngo',
    label: msg`NGO / Non-Profit`,
    category: 'Corporate & Professional',
    desc: msg`A non-governmental organisation running literacy or education programs.`,
  },
  {
    id: 'gov_ministry',
    label: msg`Government / Ministry`,
    category: 'Corporate & Professional',
    desc: msg`A public body managing national curricula or student support at scale.`,
  },
  {
    id: 'professional_body',
    label: msg`Professional Body`,
    category: 'Corporate & Professional',
    desc: msg`A sector association offering certification pathways and knowledge resources.`,
  },
  {
    id: 'incubator',
    label: msg`Incubator / Accelerator`,
    category: 'Corporate & Professional',
    desc: msg`A startup support programme teaching entrepreneurship and business skills.`,
  },
  {
    id: 'other_org',
    label: msg`Other Organisation`,
    category: 'Other',
    desc: msg`Your organisation type is not listed above.`,
  },
]

export const TOPICS_DATA: Record<string, string[]> = {
  'Scholarly Subjects': [
    'Languages',
    'Sciences',
    'Mathematics',
    'Technologies',
    'Social Sciences',
    'Arts & Humanities',
    'Business & Economics',
  ],
  'Skills & Personal Growth': [
    'Life Skills',
    'Emotional Intelligence',
    'Storytelling',
    'Mental Calculation',
    'Psychology',
    'Coding',
    'Ethics',
    'Public Speaking',
    'STEM',
    'Test Preparation',
  ],
  'Mind & Enlightenment': [
    'Philosophy',
    'Mindfulness & Meditation',
    'Spirituality',
    'Personal Development',
    'Critical Thinking',
  ],
  'Arts & Creativity': [
    'Visual Arts',
    'Music',
    'Photography & Film',
    'Writing & Poetry',
    'Performing Arts',
  ],
  'Sport & Body': [
    'Fitness & Training',
    'Nutrition',
    'Martial Arts',
    'Dance',
    'Outdoor & Adventure Sports',
  ],
  'Media & Influence': [
    'Content Creation',
    'Digital Marketing',
    'Podcasting',
    'Journalism & Commentary',
    'Entrepreneurship',
  ],
  'AI & Future Skills': [
    'AI Literacy',
    'Prompt Engineering',
    'Human + AI Collaboration',
    'Automation & Future of Work',
  ],
}

export const CATEGORY_LABELS: Record<string, MessageDescriptor> = {
  'Scholarly Subjects': msg`Scholarly Subjects`,
  'Skills & Personal Growth': msg`Skills & Personal Growth`,
  'Mind & Enlightenment': msg`Mind & Enlightenment`,
  'Arts & Creativity': msg`Arts & Creativity`,
  'Sport & Body': msg`Sport & Body`,
  'Media & Influence': msg`Media & Influence`,
  'AI & Future Skills': msg`AI & Future Skills`,
}

export const TOPIC_LABELS: Record<string, MessageDescriptor> = {
  // Scholarly Subjects
  Languages: msg`Languages`,
  Sciences: msg`Sciences`,
  Mathematics: msg`Mathematics`,
  Technologies: msg`Technologies`,
  'Social Sciences': msg`Social Sciences`,
  'Arts & Humanities': msg`Arts & Humanities`,
  'Business & Economics': msg`Business & Economics`,
  // Skills & Personal Growth
  'Life Skills': msg`Life Skills`,
  'Emotional Intelligence': msg`Emotional Intelligence`,
  Storytelling: msg`Storytelling`,
  'Mental Calculation': msg`Mental Calculation`,
  Psychology: msg`Psychology`,
  Coding: msg`Coding`,
  Ethics: msg`Ethics`,
  'Public Speaking': msg`Public Speaking`,
  STEM: msg`STEM`,
  'Test Preparation': msg`Test Preparation`,
  // Mind & Enlightenment
  Philosophy: msg`Philosophy`,
  'Mindfulness & Meditation': msg`Mindfulness & Meditation`,
  Spirituality: msg`Spirituality`,
  'Personal Development': msg`Personal Development`,
  'Critical Thinking': msg`Critical Thinking`,
  // Arts & Creativity
  'Visual Arts': msg`Visual Arts`,
  Music: msg`Music`,
  'Photography & Film': msg`Photography & Film`,
  'Writing & Poetry': msg`Writing & Poetry`,
  'Performing Arts': msg`Performing Arts`,
  // Sport & Body
  'Fitness & Training': msg`Fitness & Training`,
  Nutrition: msg`Nutrition`,
  'Martial Arts': msg`Martial Arts`,
  Dance: msg`Dance`,
  'Outdoor & Adventure Sports': msg`Outdoor & Adventure Sports`,
  // Media & Influence
  'Content Creation': msg`Content Creation`,
  'Digital Marketing': msg`Digital Marketing`,
  Podcasting: msg`Podcasting`,
  'Journalism & Commentary': msg`Journalism & Commentary`,
  Entrepreneurship: msg`Entrepreneurship`,
  // AI & Future Skills
  'AI Literacy': msg`AI Literacy`,
  'Prompt Engineering': msg`Prompt Engineering`,
  'Human + AI Collaboration': msg`Human + AI Collaboration`,
  'Automation & Future of Work': msg`Automation & Future of Work`,
}

export const TOPIC_ICONS: Record<string, string> = {
  Languages: '🗣️',
  Sciences: '🔬',
  Mathematics: '🔢',
  Technologies: '💻',
  'Social Sciences': '🌐',
  'Arts & Humanities': '🎨',
  'Business & Economics': '💼',
  'Life Skills': '🌟',
  'Emotional Intelligence': '❤️',
  Storytelling: '📖',
  'Mental Calculation': '🧮',
  Psychology: '🧠',
  Coding: '⌨️',
  Ethics: '⚖️',
  'Public Speaking': '🎤',
  STEM: '🔭',
  'Test Preparation': '📝',
  Philosophy: '🤔',
  'Mindfulness & Meditation': '🧘',
  Spirituality: '✨',
  'Personal Development': '🚀',
  'Critical Thinking': '💡',
  'Visual Arts': '🖼️',
  Music: '🎵',
  'Photography & Film': '📷',
  'Writing & Poetry': '✍️',
  'Performing Arts': '🎭',
  'Fitness & Training': '💪',
  Nutrition: '🥗',
  'Martial Arts': '🥋',
  Dance: '💃',
  'Outdoor & Adventure Sports': '🏕️',
  'Content Creation': '📱',
  'Digital Marketing': '📊',
  Podcasting: '🎙️',
  'Journalism & Commentary': '📰',
  Entrepreneurship: '🏁',
  'AI Literacy': '🤖',
  'Prompt Engineering': '⚡',
  'Human + AI Collaboration': '🤝',
  'Automation & Future of Work': '🔄',
}

export const TOPIC_MIN_LEVEL: Record<string, number> = {
  'Mental Calculation': 1,
  Psychology: 2,
  Coding: 1,
  Ethics: 2,
  'Public Speaking': 1,
  'Test Preparation': 1,
  Philosophy: 2,
  Spirituality: 3,
  'Personal Development': 1,
  'Critical Thinking': 1,
  Languages: 1,
  Sciences: 1,
  Mathematics: 1,
  Technologies: 1,
  'Social Sciences': 2,
  'Arts & Humanities': 2,
  'Business & Economics': 3,
  'Content Creation': 1,
  'Digital Marketing': 3,
  Podcasting: 2,
  'Journalism & Commentary': 2,
  Entrepreneurship: 3,
  'AI Literacy': 1,
  'Prompt Engineering': 3,
  'Human + AI Collaboration': 2,
  'Automation & Future of Work': 3,
}

export const STAGE_LEVEL: Record<string, number> = {
  'Preschool / Kindergarten': 0,
  'Primary school': 1,
  'Middle School': 2,
  'High School': 3,
  University: 4,
  'Self-learner (autodidact)': 4,
  'Lifelong Learner': 4,
  'Self-learners': 4,
  Professionals: 4,
  Parents: 3,
}

export const LEARNING_STAGE_ICONS: Record<string, string> = {
  'Preschool / Kindergarten': '🌈',
  'Primary school': '✏️',
  'Middle School': '📚',
  'High School': '🎒',
  University: '🎓',
  'Self-learner (autodidact)': '🔍',
  'Lifelong Learner': '♾️',
}

export const FAMILY_STAGE_ICONS: Record<string, string> = {
  'Preschool / Kindergarten': '🌈',
  'Primary school': '✏️',
  'Middle School': '📚',
  'High School': '🎒',
  University: '🎓',
}

export const AUDIENCE_ICONS: Record<string, string> = {
  'Preschool / Kindergarten': '🌈',
  'Primary school': '✏️',
  'Middle School': '📚',
  'High School': '🎒',
  University: '🎓',
  'Self-learners': '🔍',
  Professionals: '💼',
  Parents: '👨‍👩‍👧',
}

export const ORG_TYPE_ICONS: Record<string, string> = {
  school_primary: '🏫',
  school_middle: '🏫',
  school_secondary: '🏫',
  school_private: '🏫',
  university: '🎓',
  grande_ecole: '🏛️',
  vocational: '🔧',
  centre_formation: '📋',
  tutoring_center: '📖',
  language_school: '🗣️',
  bootcamp: '💻',
  online_school: '🌐',
  library: '📚',
  museum: '🏛️',
  maison_jeunes: '🏠',
  cultural_assoc: '🎭',
  mosque_school: '🕌',
  book_publisher: '📗',
  bookstore: '📚',
  media_org: '📺',
  press: '📰',
  company_hr: '🏢',
  edtech_company: '💡',
  ngo: '🤝',
  gov_ministry: '🏛️',
  professional_body: '🏆',
  incubator: '🚀',
  other_org: '🏢',
}

export type CountryCurriculum = {
  name: string
  flag: string
  stages: Record<string, {icon: string; levels: string[]}>
}

export const COUNTRY_CURRICULA: Record<string, CountryCurriculum> = {
  TN: {
    name: 'Tunisia',
    flag: '🇹🇳',
    stages: {
      'Primary school': {
        icon: 'child',
        levels: [
          '1st year',
          '2nd year',
          '3rd year',
          '4th year',
          '5th year',
          '6th year',
        ],
      },
      'Middle School': {
        icon: 'student',
        levels: ['7th year', '8th year', '9th year'],
      },
      'High School': {
        icon: 'graduate',
        levels: ['1st year', '2nd year', '3rd year', 'BAC'],
      },
    },
  },
  DZ: {
    name: 'Algeria',
    flag: '🇩🇿',
    stages: {
      'Primary school': {
        icon: 'child',
        levels: ['1ere AP', '2eme AP', '3eme AP', '4eme AP', '5eme AP'],
      },
      'Middle School': {
        icon: 'student',
        levels: ['1ere AM', '2eme AM', '3eme AM', '4eme AM'],
      },
      'High School': {
        icon: 'graduate',
        levels: ['1ere AS', '2eme AS', '3eme AS', 'BAC'],
      },
    },
  },
  MA: {
    name: 'Morocco',
    flag: '🇲🇦',
    stages: {
      'Primary school': {
        icon: 'child',
        levels: [
          '1ere annee',
          '2eme annee',
          '3eme annee',
          '4eme annee',
          '5eme annee',
          '6eme annee',
        ],
      },
      'Middle School': {
        icon: 'student',
        levels: ['1ere AC', '2eme AC', '3eme AC'],
      },
      'High School': {
        icon: 'graduate',
        levels: ['Tronc commun', '1ere Bac', '2eme Bac'],
      },
    },
  },
  EG: {
    name: 'Egypt',
    flag: '🇪🇬',
    stages: {
      'Primary school': {
        icon: 'child',
        levels: [
          'Grade 1',
          'Grade 2',
          'Grade 3',
          'Grade 4',
          'Grade 5',
          'Grade 6',
        ],
      },
      'Middle School': {
        icon: 'student',
        levels: ['Prep 1', 'Prep 2', 'Prep 3'],
      },
      'High School': {
        icon: 'graduate',
        levels: ['Sec 1', 'Sec 2', 'Sec 3 (Thanawy)'],
      },
    },
  },
  LY: {
    name: 'Libya',
    flag: '🇱🇾',
    stages: {
      'Primary school': {
        icon: 'child',
        levels: [
          'Grade 1',
          'Grade 2',
          'Grade 3',
          'Grade 4',
          'Grade 5',
          'Grade 6',
        ],
      },
      'Middle School': {
        icon: 'student',
        levels: ['Grade 7', 'Grade 8', 'Grade 9'],
      },
      'High School': {
        icon: 'graduate',
        levels: ['Grade 10', 'Grade 11', 'Grade 12', 'Tawjihi'],
      },
    },
  },
  NG: {
    name: 'Nigeria',
    flag: '🇳🇬',
    stages: {
      'Primary school': {
        icon: 'child',
        levels: [
          'Primary 1',
          'Primary 2',
          'Primary 3',
          'Primary 4',
          'Primary 5',
          'Primary 6',
        ],
      },
      'Middle School': {
        icon: 'student',
        levels: ['JSS 1', 'JSS 2', 'JSS 3'],
      },
      'High School': {
        icon: 'graduate',
        levels: ['SSS 1', 'SSS 2', 'SSS 3'],
      },
    },
  },
  SA: {
    name: 'Saudi Arabia',
    flag: '🇸🇦',
    stages: {
      'Primary school': {
        icon: 'child',
        levels: [
          'Grade 1',
          'Grade 2',
          'Grade 3',
          'Grade 4',
          'Grade 5',
          'Grade 6',
        ],
      },
      'Middle School': {
        icon: 'student',
        levels: ['Grade 7', 'Grade 8', 'Grade 9'],
      },
      'High School': {
        icon: 'graduate',
        levels: ['Grade 10', 'Grade 11', 'Grade 12'],
      },
    },
  },
  AE: {
    name: 'United Arab Emirates',
    flag: '🇦🇪',
    stages: {
      'Primary school': {
        icon: 'child',
        levels: ['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5'],
      },
      'Middle School': {
        icon: 'student',
        levels: ['Grade 6', 'Grade 7', 'Grade 8', 'Grade 9'],
      },
      'High School': {
        icon: 'graduate',
        levels: ['Grade 10', 'Grade 11', 'Grade 12'],
      },
    },
  },
  JO: {
    name: 'Jordan',
    flag: '🇯🇴',
    stages: {
      'Primary school': {
        icon: 'child',
        levels: [
          'Grade 1',
          'Grade 2',
          'Grade 3',
          'Grade 4',
          'Grade 5',
          'Grade 6',
        ],
      },
      'Middle School': {
        icon: 'student',
        levels: ['Grade 7', 'Grade 8', 'Grade 9', 'Grade 10'],
      },
      'High School': {
        icon: 'graduate',
        levels: ['Grade 11', 'Grade 12 (Tawjihi)'],
      },
    },
  },
  QA: {
    name: 'Qatar',
    flag: '🇶🇦',
    stages: {
      'Primary school': {
        icon: 'child',
        levels: [
          'Grade 1',
          'Grade 2',
          'Grade 3',
          'Grade 4',
          'Grade 5',
          'Grade 6',
        ],
      },
      'Middle School': {
        icon: 'student',
        levels: ['Grade 7', 'Grade 8', 'Grade 9'],
      },
      'High School': {
        icon: 'graduate',
        levels: ['Grade 10', 'Grade 11', 'Grade 12'],
      },
    },
  },
  KW: {
    name: 'Kuwait',
    flag: '🇰🇼',
    stages: {
      'Primary school': {
        icon: 'child',
        levels: ['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5'],
      },
      'Middle School': {
        icon: 'student',
        levels: ['Grade 6', 'Grade 7', 'Grade 8', 'Grade 9'],
      },
      'High School': {
        icon: 'graduate',
        levels: ['Grade 10', 'Grade 11', 'Grade 12'],
      },
    },
  },
  LB: {
    name: 'Lebanon',
    flag: '🇱🇧',
    stages: {
      'Primary school': {
        icon: 'child',
        levels: [
          'Grade 1',
          'Grade 2',
          'Grade 3',
          'Grade 4',
          'Grade 5',
          'Grade 6',
        ],
      },
      'Middle School': {
        icon: 'student',
        levels: ['Complémentaire 1', 'Complémentaire 2', 'Complémentaire 3'],
      },
      'High School': {
        icon: 'graduate',
        levels: ['1ère secondaire', '2ème secondaire', '3ème secondaire (BAC)'],
      },
    },
  },
  FR: {
    name: 'France',
    flag: '🇫🇷',
    stages: {
      'Primary school': {
        icon: 'child',
        levels: ['CP', 'CE1', 'CE2', 'CM1', 'CM2'],
      },
      'Middle School': {icon: 'student', levels: ['6e', '5e', '4e', '3e']},
      'High School': {icon: 'graduate', levels: ['2nde', '1ere', 'Terminale']},
    },
  },
  GB: {
    name: 'United Kingdom',
    flag: '🇬🇧',
    stages: {
      'Primary school': {
        icon: 'child',
        levels: ['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6'],
      },
      'Middle School': {
        icon: 'student',
        levels: ['Year 7', 'Year 8', 'Year 9'],
      },
      'High School': {
        icon: 'graduate',
        levels: ['Year 10', 'Year 11', 'Year 12', 'Year 13'],
      },
    },
  },
  US: {
    name: 'United States',
    flag: '🇺🇸',
    stages: {
      'Primary school': {
        icon: 'child',
        levels: [
          'Kindergarten',
          '1st grade',
          '2nd grade',
          '3rd grade',
          '4th grade',
          '5th grade',
        ],
      },
      'Middle School': {
        icon: 'student',
        levels: ['6th grade', '7th grade', '8th grade'],
      },
      'High School': {
        icon: 'graduate',
        levels: ['9th grade', '10th grade', '11th grade', '12th grade'],
      },
    },
  },
  TR: {
    name: 'Turkey',
    flag: '🇹🇷',
    stages: {
      'Primary school': {
        icon: 'child',
        levels: ['1. Sinif', '2. Sinif', '3. Sinif', '4. Sinif'],
      },
      'Middle School': {
        icon: 'student',
        levels: ['5. Sinif', '6. Sinif', '7. Sinif', '8. Sinif'],
      },
      'High School': {
        icon: 'graduate',
        levels: ['9. Sinif', '10. Sinif', '11. Sinif', '12. Sinif'],
      },
    },
  },
  OTHER: {
    name: 'Other / not listed',
    flag: '🌍',
    stages: {
      'Primary school': {
        icon: 'child',
        levels: [
          'Grade 1',
          'Grade 2',
          'Grade 3',
          'Grade 4',
          'Grade 5',
          'Grade 6',
        ],
      },
      'Middle School': {
        icon: 'student',
        levels: ['Grade 7', 'Grade 8', 'Grade 9'],
      },
      'High School': {
        icon: 'graduate',
        levels: ['Grade 10', 'Grade 11', 'Grade 12'],
      },
    },
  },
}

export const COUNTRY_LIST = Object.entries(COUNTRY_CURRICULA).map(
  ([code, c]) => ({code, name: c.name, flag: c.flag}),
)

export type AudienceCopy = {label: string; pronoun: string}

export const AUDIENCE_COPY: Record<string, AudienceCopy> = {
  'Preschool / Kindergarten': {
    label: 'Little Learners',
    pronoun: 'little learners',
  },
  'Primary school': {
    label: 'Young Minds',
    pronoun: 'young minds',
  },
  'Middle School': {
    label: 'Teens',
    pronoun: 'teens',
  },
  'High School': {
    label: 'High Schoolers',
    pronoun: 'high schoolers',
  },
  University: {
    label: 'University Students',
    pronoun: 'university students',
  },
  'Self-learners': {
    label: 'Self-Learners',
    pronoun: 'self-learners',
  },
  Professionals: {
    label: 'Professionals',
    pronoun: 'professionals',
  },
  Parents: {
    label: 'Parents',
    pronoun: 'parents',
  },
}

export function getAudienceCopy(audience: string[]): AudienceCopy {
  if (!audience || audience.length === 0)
    return {label: 'Your Audience', pronoun: 'your audience'}
  if (audience.length === 1) {
    return (
      AUDIENCE_COPY[audience[0]] ?? {
        label: audience[0],
        pronoun: audience[0].toLowerCase(),
      }
    )
  }
  const a1 = AUDIENCE_COPY[audience[0]] ?? {
    label: audience[0],
    pronoun: audience[0].toLowerCase(),
  }
  const a2 = AUDIENCE_COPY[audience[1]] ?? {
    label: audience[1],
    pronoun: audience[1].toLowerCase(),
  }
  return {
    label: `${a1.label} & ${a2.label}`,
    pronoun: `${a1.pronoun} and ${a2.pronoun}`,
  }
}

export const LEARNER_STAGE_CONFIRMED_COPY: Record<string, string> = {
  'Preschool / Kindergarten':
    "We'll curate magical discovery moments just for you!",
  'Primary school':
    "We'll guide you through the fundamentals with fun learning.",
  'Middle School': 'Your feed will focus on core subjects and skill building.',
  'High School': "We'll focus on exam prep, specialisation, and career skills.",
  University: 'Deep dives, research, and professional topics await!',
  'Self-learner (autodidact)':
    "No limits! We'll build a feed around your chosen topics.",
  'Lifelong Learner':
    'Always growing — your feed will blend curiosity with practical wisdom.',
}

export const STAGE_TO_CHILD_LABEL: Record<
  string,
  {noun: string; emoji: string}
> = {
  'Preschool / Kindergarten': {noun: 'preschooler', emoji: '✨'},
  'Primary school': {noun: 'primary-schooler', emoji: '📚'},
  'Middle School': {noun: 'middle-schooler', emoji: '🧑‍🎓'},
  'High School': {noun: 'high-schooler', emoji: '🎒'},
  University: {noun: 'university student', emoji: '🎓'},
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export function getChildTopicsCopy(
  familyStages: string[],
  max: number,
): {title: string; sub: string} {
  if (!familyStages || familyStages.length === 0) {
    return {
      title: 'Skills for Your Children 📚',
      sub: `Pick up to ${max} topics to help your children thrive.`,
    }
  }
  if (familyStages.length === 1) {
    const info = STAGE_TO_CHILD_LABEL[familyStages[0]] ?? {
      noun: 'child',
      emoji: '📚',
    }
    return {
      title: `Topics for Your ${capitalize(info.noun)} ${info.emoji}`,
      sub: `Pick up to ${max} topics to help your ${info.noun} thrive.`,
    }
  }
  // Sort by FAMILY_STAGES order so the range reads youngest to oldest
  const ordered = FAMILY_STAGES.filter(s => familyStages.includes(s))
  const count = familyStages.length
  const nouns = ordered.map(
    st => (STAGE_TO_CHILD_LABEL[st] ?? {noun: 'child'}).noun,
  )
  const range = `${nouns[0]} to ${nouns[nouns.length - 1]}`
  return {
    title: `Topics for Your ${count} Kids 📚`,
    sub: `From ${range} — pick up to ${max} topics across all your children's interests.`,
  }
}

export const LEARNER_STAGE_TOPICS_TITLE: Record<string, string> = {
  'Preschool / Kindergarten': 'What do you love to discover?',
  'Primary school': 'What subjects do you want to explore?',
  'Middle School': 'What do you want to get better at?',
  'High School': 'What subjects and skills matter most to you?',
  University: 'What are you studying or curious about?',
  'Self-learner (autodidact)': 'What topics are you teaching yourself?',
  'Lifelong Learner': 'What are you curious about right now?',
}

type RoleCopy = {
  title: (audienceLabel: string) => string
  sub: (pronoun: string, max: number) => string
}

export const CREATOR_ROLE_TOPICS_COPY: Record<string, RoleCopy> = {
  tutor: {
    title: aud => `Break It Down for ${aud} 📖`,
    sub: (pronoun, max) =>
      `Your lessons shape how ${pronoun} master the fundamentals. Pick up to ${max} subjects to teach.`,
  },
  coach: {
    title: aud => `Coach ${aud} to Their Best 🏆`,
    sub: (pronoun, max) =>
      `Coaching creates lasting change. Pick the areas where you'll challenge ${pronoun} the furthest — up to ${max}.`,
  },
  mentor: {
    title: aud => `Mentor ${aud} Forward 🧭`,
    sub: (pronoun, max) =>
      `Your lived experience is their shortcut. Pick up to ${max} topics that shaped your path — and will shape theirs.`,
  },
  trainer: {
    title: aud => `Train ${aud} to Perform 🏋️`,
    sub: (pronoun, max) =>
      `Training is hands-on and measurable. Pick up to ${max} domains where you'll push ${pronoun} further.`,
  },
  guide: {
    title: aud => `Lead ${aud} to Clarity 🗺️`,
    sub: (pronoun, max) =>
      `You bring direction where there's noise. Pick up to ${max} areas where ${pronoun} need grounding most.`,
  },
  artist: {
    title: aud => `Inspire ${aud} Through Craft 🎨`,
    sub: (pronoun, max) =>
      `Art teaches what words can't. Pick up to ${max} creative areas you'll share with ${pronoun}.`,
  },
  speaker: {
    title: aud => `Speak to ${aud} — and Move Them 🎤`,
    sub: (pronoun, max) =>
      `Every talk lands differently depending on who's listening. Pick up to ${max} topics you'll deliver for ${pronoun}.`,
  },
  creator: {
    title: aud => `Content ${aud} Will Follow 🎬`,
    sub: (pronoun, max) =>
      `Pick up to ${max} topics ${pronoun} watch, share, and come back for. Make every post count.`,
  },
  researcher: {
    title: aud => `Research ${aud} Can Trust 🔬`,
    sub: (pronoun, max) =>
      `You go deeper so ${pronoun} don't have to. Pick up to ${max} fields where your findings will matter most.`,
  },
  consultant: {
    title: aud => `Solve What ${aud} Can't 💼`,
    sub: (pronoun, max) =>
      `Consulting is results, not theory. Pick up to ${max} domains where you tackle ${pronoun}'s hardest problems.`,
  },
  podcaster: {
    title: aud => `Conversations ${aud} Needs to Hear 🎙️`,
    sub: (pronoun, max) =>
      `The right topic makes ${pronoun} hit play again. Pick up to ${max} themes your podcast will explore.`,
  },
  edutainment: {
    title: aud => `Make Learning Unforgettable for ${aud} 🎭`,
    sub: (pronoun, max) =>
      `If ${pronoun} laugh, they learn. Pick up to ${max} topics you'll bring to life with storytelling and fun.`,
  },
  ai_skill_trainer: {
    title: aud => `AI That Actually Works for ${aud} 🤖`,
    sub: (pronoun, max) =>
      `You close the gap between AI hype and real use. Pick up to ${max} domains where you'll upskill ${pronoun}.`,
  },
}

export const ORG_TYPE_TOPICS_COPY: Record<string, RoleCopy> = {
  school_primary: {
    title: aud => `Subjects & Skills for ${aud} 📚`,
    sub: (p, m) =>
      `Shape what ${p} discover. Pick up to ${m} topic areas your school will cover.`,
  },
  school_middle: {
    title: aud => `Curriculum Focus for ${aud} 🧑‍🎓`,
    sub: (p, m) =>
      `Build the foundation that carries ${p} into high school. Select up to ${m} areas.`,
  },
  school_secondary: {
    title: aud => `Preparing ${aud} for What's Next 🎓`,
    sub: (p, m) =>
      `From BAC prep to beyond — pick up to ${m} subjects and skills your school champions.`,
  },
  school_private: {
    title: aud => `Your Academic Offer for ${aud} 🏛️`,
    sub: (p, m) =>
      `Define the learning identity of your school. Select up to ${m} topic pillars.`,
  },
  university: {
    title: aud => `Disciplines & Research for ${aud} 🔬`,
    sub: (p, m) =>
      `From lectures to labs — pick the ${m} academic pillars your faculty publishes around.`,
  },
  grande_ecole: {
    title: aud => `Fields of Excellence for ${aud} 🏆`,
    sub: (p, m) =>
      `Your institution stands for precision and mastery. Select up to ${m} areas of focus.`,
  },
  vocational: {
    title: aud => `Trades & Certifications for ${aud} 🔧`,
    sub: (p, m) =>
      `Hands-on, career-ready skills. Pick up to ${m} domains your centre trains people in.`,
  },
  centre_formation: {
    title: aud => `Training Programmes for ${aud} 📋`,
    sub: (p, m) =>
      `Define your curriculum. Select up to ${m} professional development areas.`,
  },
  tutoring_center: {
    title: aud => `Support Areas for ${aud} 📖`,
    sub: (p, m) =>
      `Where do ${p} need the most support? Pick up to ${m} reinforcement topics.`,
  },
  language_school: {
    title: aud => `Languages & Communication for ${aud} 🗣️`,
    sub: (p, m) =>
      `Pick the linguistic and communication skills you'll deliver to ${p} — up to ${m}.`,
  },
  bootcamp: {
    title: aud => `Skills ${aud} Will Ship With 💻`,
    sub: (p, m) =>
      `Intense. Practical. Job-ready. Pick up to ${m} skill areas your bootcamp covers.`,
  },
  online_school: {
    title: aud => `Courses ${aud} Will Love 🌐`,
    sub: (p, m) =>
      `Your catalogue starts here. Pick up to ${m} topic categories your platform will publish.`,
  },
  library: {
    title: aud => `Collections for ${aud} 📚`,
    sub: (p, m) =>
      `Curate the knowledge your library makes accessible. Select up to ${m} subject areas.`,
  },
  museum: {
    title: aud => `Themes & Programmes for ${aud} 🏛️`,
    sub: (p, m) =>
      `From exhibitions to workshops — pick up to ${m} areas your institution teaches through.`,
  },
  maison_jeunes: {
    title: aud => `Activities & Growth for ${aud} 🏠`,
    sub: (p, m) =>
      `What will you offer young people? Pick up to ${m} focus areas for your centre.`,
  },
  cultural_assoc: {
    title: _aud => `What Your Association Stands For 🎭`,
    sub: (p, m) =>
      `Pick up to ${m} topics your association teaches, promotes, or advocates for.`,
  },
  mosque_school: {
    title: aud => `Values & Knowledge for ${aud} 🕌`,
    sub: (p, m) =>
      `Pick up to ${m} areas of learning and growth your school nurtures.`,
  },
  book_publisher: {
    title: aud => `Catalogue Focus for ${aud} 📗`,
    sub: (p, m) =>
      `Which subjects does your publishing house serve? Pick up to ${m} topic areas.`,
  },
  bookstore: {
    title: aud => `Reading Themes for ${aud} 📚`,
    sub: (p, m) =>
      `What do ${p} come to your shelves to find? Select up to ${m} topic categories.`,
  },
  media_org: {
    title: aud => `Content Pillars for ${aud} 📺`,
    sub: (p, m) =>
      `Pick up to ${m} topic areas your media organisation covers and produces content around.`,
  },
  press: {
    title: _aud => `Topics Your Publication Covers 📰`,
    sub: (p, m) =>
      `Define your editorial focus for ${p}. Select up to ${m} subject pillars.`,
  },
  company_hr: {
    title: _aud => `Skills Your Team Needs 🏢`,
    sub: (p, m) =>
      `Pick up to ${m} learning domains for your internal training and development programme.`,
  },
  edtech_company: {
    title: _aud => `Topics Your Product Covers 💡`,
    sub: (p, m) =>
      `What does your platform teach ${p}? Define up to ${m} topic areas.`,
  },
  ngo: {
    title: aud => `Impact Areas for ${aud} 🤝`,
    sub: (p, m) =>
      `Pick up to ${m} topics your NGO delivers skills and knowledge around.`,
  },
  gov_ministry: {
    title: aud => `Curriculum Priorities for ${aud} 🏛️`,
    sub: (p, m) =>
      `Select up to ${m} subject areas your ministry publishes official content for.`,
  },
  professional_body: {
    title: aud => `CPD & Certification Focus for ${aud} 🏆`,
    sub: (p, m) =>
      `Select up to ${m} professional domains your body accredits and teaches.`,
  },
  incubator: {
    title: aud => `What You Build in ${aud} 🚀`,
    sub: (p, m) =>
      `Pick up to ${m} skills and topics your programme instils in future founders.`,
  },
  other_org: {
    title: aud => `What Will You Share with ${aud}? 🏢`,
    sub: (p, m) =>
      `Pick up to ${m} topic areas that best describe your organisation's knowledge focus.`,
  },
}

export function findCreatorRole(
  id: string | undefined,
): CreatorRole | undefined {
  if (!id) return undefined
  return CREATOR_ROLES.find(r => r.id === id)
}

export function findOrgType(id: string | undefined): OrgType | undefined {
  if (!id) return undefined
  return ORG_TYPES.find(r => r.id === id)
}

export function getCreatorTopicsCopy(
  role: string | null,
  audience: string[],
  max: number,
): {title: string; sub: string} {
  const {label, pronoun} = getAudienceCopy(audience)
  const copy = role ? CREATOR_ROLE_TOPICS_COPY[role] : undefined
  if (copy) return {title: copy.title(label), sub: copy.sub(pronoun, max)}
  return {
    title: `What Will You Teach the World? 📚`,
    sub: `Select up to ${max} topics that define your content focus.`,
  }
}

export function getOrgTopicsCopy(
  orgType: string | null,
  audience: string[],
  max: number,
): {title: string; sub: string} {
  const {label, pronoun} = getAudienceCopy(audience)
  const copy = orgType ? ORG_TYPE_TOPICS_COPY[orgType] : undefined
  if (copy) return {title: copy.title(label), sub: copy.sub(pronoun, max)}
  return {
    title: `What Does Your Organisation Cover? 🏢`,
    sub: `Select up to ${max} topic areas that define your institution's focus.`,
  }
}

export function getLearnerTopicsCopy(
  stage: string | null,
  max: number,
): {title: string; sub: string} {
  const schoolStages = ['Primary school', 'Middle School', 'High School']
  if (!stage || schoolStages.includes(stage)) {
    return {
      title: 'Level Up Beyond the Classroom 📚',
      sub: `Your subjects are just the start — pick the skills and passions that sharpen how you think, learn, and grow. Select up to ${max}.`,
    }
  }
  if (stage === 'Preschool / Kindergarten') {
    return {
      title: 'What Sparks Your Curiosity? ✨',
      sub: `No classroom, no limits — pick what you want to discover and explore. Select up to ${max}.`,
    }
  }
  if (stage === 'University') {
    return {
      title: 'Level Up Your Expertise 🎓',
      sub: `Go beyond your degree — pick the skills and interests that shape who you become. Select up to ${max}.`,
    }
  }
  if (stage === 'Self-learner (autodidact)') {
    return {
      title: 'What Are You Here to Master? 🔥',
      sub: `Build your own curriculum — pick what excites and challenges you. Select up to ${max}.`,
    }
  }
  if (stage === 'Lifelong Learner') {
    return {
      title: 'What Feeds Your Curiosity? 📚',
      sub: `Choose the topics that keep your mind alive and growing. Select up to ${max}.`,
    }
  }
  return {
    title: 'Level Up Beyond the Classroom 📚',
    sub: `Your subjects are just the start — pick the skills and passions that sharpen how you think, learn, and grow. Select up to ${max}.`,
  }
}

export function getStageLevel(stageOrAudience: string): number {
  return STAGE_LEVEL[stageOrAudience] ?? 4
}

export function getFilteredTopics(
  level: number,
  excludeCategories: string[] = [],
): Record<string, string[]> {
  const result: Record<string, string[]> = {}
  for (const [cat, items] of Object.entries(TOPICS_DATA)) {
    if (excludeCategories.includes(cat)) continue
    const filtered = items.filter(item => (TOPIC_MIN_LEVEL[item] ?? 0) <= level)
    if (filtered.length > 0) result[cat] = filtered
  }
  return result
}

export function resolveTopicsLevel(g: GoalStepResults, step: string): number {
  if (g.accountType === 'learner') return getStageLevel(g.learningStage ?? '')
  if (g.accountType === 'parent') {
    if (step === 'goal-parent-topics') return 4
    return Math.max(...g.familyStages.map(getStageLevel), 0)
  }
  if (g.accountType === 'creator') {
    return Math.max(...g.audience.map(getStageLevel), 0)
  }
  if (g.accountType === 'organisation') {
    return 4
  }
  return 4
}

export function buildPayload(g: GoalStepResults): Record<string, unknown> {
  if (!g.accountType || !g.country || !g.displayName) {
    console.warn('buildPayload: missing required fields', {
      accountType: g.accountType,
      country: g.country,
      hasDisplayName: !!g.displayName,
    })
  }
  const base = {
    accountType: g.accountType,
    country: g.country,
    displayName: g.displayName,
  }
  switch (g.accountType) {
    case 'learner':
      return {
        ...base,
        ...(g.learningStage ? {learningStage: g.learningStage} : {}),
        ...(g.gradeLevel ? {gradeLevel: g.gradeLevel} : {}),
        ...(g.topics.length ? {topics: g.topics} : {}),
      }
    case 'parent':
      return {
        ...base,
        ...(g.familyStages.length ? {familyStages: g.familyStages} : {}),
        ...(g.parentTopics.length ? {parentTopics: g.parentTopics} : {}),
        ...(g.childTopics.length ? {childTopics: g.childTopics} : {}),
      }
    case 'creator':
      return {
        ...base,
        ...(g.creatorRole ? {creatorRole: g.creatorRole} : {}),
        ...(g.audience.length ? {audience: g.audience} : {}),
        ...(g.topics.length ? {topics: g.topics} : {}),
      }
    case 'organisation':
      return {
        ...base,
        ...(g.orgType ? {orgType: g.orgType} : {}),
        ...(g.orgName ? {orgName: g.orgName} : {}),
        ...(g.audience.length ? {orgAudience: g.audience} : {}),
        ...(g.topics.length ? {orgTopics: g.topics} : {}),
      }
    default:
      return base
  }
}
